import React, { useEffect, useState } from "react";
import { CircularProgress, Box, Grid2 as Grid, Skeleton, Typography } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";

import { Form, ProductsAutocomplete } from "@shared/components";
import { getProductByIdService } from "@modules/defaultProducts/services/realService";
import { ProductMetadata } from "@modules/defaultProducts/types/product";
import { useListSectorsQuery } from "@modules/bookSectors/services/queries";

export function AgreementProductForm({ isOpenMod }: { isOpenMod: boolean }) {
  const methods = useFormContext();
  const [isLoading, setIsLoading] = useState(true)
  const selectedProductId = useWatch({
    control: methods.control,
    name: "default_product",
  });
  const [defaultMetadata, setDefaultMetadata] = useState<ProductMetadata[]>([]);
  const shouldFetchSectors = defaultMetadata.some((meta) => meta.type === "select_categories_library");
  const { data: sectors = [], isLoading: isLoadingSectors } = useListSectorsQuery(
    { perPage: 100 },
    { enabled: shouldFetchSectors }
  );

  console.log("selectedProductId", selectedProductId);
  console.log("isOpenMod", isOpenMod);
  useEffect(() => {
    const fetchDefaultMetadata = async () => {
      if (!isOpenMod) return;

      try {
        setIsLoading(true);

        if (!selectedProductId) {
          setDefaultMetadata([]);
          methods.setValue("metadata", {});
          return;
        }

        const product = await getProductByIdService(Number(selectedProductId));
        const productMetadata = product?.metadata || {};
        const metaArray = Object.values(productMetadata) as ProductMetadata[];

        const sortedMetadata = metaArray.sort((a, b) => {
          const order: Record<string, number> = {
            image: 1,
            string: 2,
            textArea: 3,
            color: 4,
            boolean: 5,
            date: 6,
          };
          return (order[a.type as keyof typeof order] || 99) - (order[b.type as keyof typeof order] || 99);
        });

        setDefaultMetadata(sortedMetadata);

        const defaultValues = methods.getValues();
        if (defaultValues?.id && defaultValues?.metadata) {
          sortedMetadata.forEach((metaDef) => {
            const raw = defaultValues.metadata?.[metaDef.key]?.value as any;
            const path = `metadata.${metaDef.key}` as const;
            switch (metaDef.type) {
              case "boolean": {
                const boolVal = typeof raw === "boolean" ? raw : ["true", "1", 1, "True", "TRUE"].includes(String(raw));
                methods.setValue(path, boolVal);
                break;
              }
              case "select_categories_library": {
                try {
                  const arr = raw ? JSON.parse(raw as string) : [];
                  methods.setValue(path, Array.isArray(arr) ? arr : []);
                } catch {
                  methods.setValue(path, []);
                }
                break;
              }
              case "number": {
                const num = raw !== undefined && raw !== null && raw !== "" ? Number(raw) : undefined;
                methods.setValue(path, Number.isNaN(num as number) ? undefined : num);
                break;
              }
              default: {
                methods.setValue(path, raw ?? "");
              }
            }
          });
        } else {
          methods.setValue("metadata", {});
        }
      } catch (_) {
        console.log("error fetching default metadata");
        setDefaultMetadata([]);
        methods.setValue("metadata", {});
      } finally {
        setIsLoading(false);
      }
    };
    fetchDefaultMetadata();
  }, [selectedProductId, isOpenMod]);

  if (isLoading) {
    return <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Box display="flex" alignItems="center" justifyContent="center" height={80}>
          <CircularProgress />
        </Box>
      </Grid>
    </Grid>
  }
  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <ProductsAutocomplete name="default_product" label="Produto" required />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Form.Input name="name" label="Nome" required />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Form.Switch name="active" label="Ativo" />
      </Grid>

      {defaultMetadata.length > 0 && (
        <>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6">Configuração do produto</Typography>
          </Grid>
          <Grid container spacing={2}>
            {defaultMetadata.map((meta) => (
              <Grid key={meta.key} size={{ xs: meta.type === "textArea" ? 12 : 6 }}>
                {meta.type === "boolean" ? (
                  <Form.Switch
                    name={`metadata.${meta.key}`}
                    label={(meta.value as unknown as string) || meta.key}
                  />
                ) : meta.type === "date" ? (
                  <Form.DatePicker
                    name={`metadata.${meta.key}`}
                    label={(meta.value as unknown as string) || meta.key}
                    required={!!meta.obrigatorio}
                  />
                ) : meta.type === "image" ? (
                  <Form.SinglePhotoPicker
                    size={150}
                    shape="square"
                    name={`metadata.${meta.key}`}
                    label={(meta.value as unknown as string) || meta.key}
                  />
                ) : meta.type === "color" ? (
                  <Form.ColorPickerInput
                    name={`metadata.${meta.key}`}
                    label={(meta.value as unknown as string) || meta.key}
                    required={!!meta.obrigatorio}
                  />
                ) : meta.type === "select_categories_library" ? (
                  isLoadingSectors ? (
                    <Skeleton variant="rounded" height={45} />
                  ) : (
                    <Form.Select
                      options={(sectors || []).map((sector: any) => ({
                        label: sector.name,
                        value: sector.id,
                      }))}
                      name={`metadata.${meta.key}`}
                      label={'Setores'}
                      multiple
                      required={!!meta.obrigatorio}
                    />
                  )
                ) : meta.type === "textArea" ? (
                  <Form.Input
                    name={`metadata.${meta.key}`}
                    label={(meta.value as unknown as string) || meta.key}
                    required={!!meta.obrigatorio}
                    multiline
                    rows={5}
                  />
                ) : (
                  <Form.Input
                    name={`metadata.${meta.key}`}
                    label={(meta.value as unknown as string) || meta.key}
                    required={!!meta.obrigatorio}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Grid>
  );
}

// removed unused SelectSetores
